import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Supports both controlled and uncontrolled usage with one API, which is how
 * every antd component behaves (`value` / `defaultValue`, `open` / `defaultOpen`).
 *
 * @returns {[any, (next: any) => void, boolean]} value, setter, isControlled
 */
export const useControllableValue = (controlled, defaultValue, onChange) => {
  const isControlled = controlled !== undefined
  const [uncontrolled, setUncontrolled] = useState(defaultValue)
  const value = isControlled ? controlled : uncontrolled

  // Extra args are forwarded to `onChange`, which antd needs for callbacks like
  // DatePicker's `(date, dateString)` and Select's `(value, option)`.
  const setValue = useCallback(
    (next, ...extra) => {
      const resolved = typeof next === 'function' ? next(value) : next
      if (!isControlled) setUncontrolled(resolved)
      onChange?.(resolved, ...extra)
    },
    [isControlled, onChange, value]
  )

  return [value, setValue, isControlled]
}

/** Latest-value ref, so event handlers registered once never read stale props. */
export const useLatest = (value) => {
  const ref = useRef(value)
  useLayoutEffect(() => {
    ref.current = value
  })
  return ref
}

export const useOnClickOutside = (refs, handler, active = true) => {
  const handlerRef = useLatest(handler)

  useEffect(() => {
    if (!active) return undefined
    const nodes = Array.isArray(refs) ? refs : [refs]

    const onPointerDown = (event) => {
      const isInside = nodes.some((ref) => ref?.current?.contains(event.target))
      if (isInside) return
      handlerRef.current?.(event)
    }

    document.addEventListener('mousedown', onPointerDown, true)
    document.addEventListener('touchstart', onPointerDown, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true)
      document.removeEventListener('touchstart', onPointerDown, true)
    }
  }, [active, handlerRef, refs])
}

export const useEscapeKey = (handler, active = true) => {
  const handlerRef = useLatest(handler)

  useEffect(() => {
    if (!active) return undefined
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return
      handlerRef.current?.(event)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [active, handlerRef])
}

/** Locks body scroll while an overlay is open, compensating for the scrollbar width. */
export const useScrollLock = (active) => {
  useEffect(() => {
    if (!active) return undefined
    const { body } = document
    const previousOverflow = body.style.overflow
    const previousPaddingRight = body.style.paddingRight
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`

    return () => {
      body.style.overflow = previousOverflow
      body.style.paddingRight = previousPaddingRight
    }
  }, [active])
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** Keeps Tab focus inside `containerRef` and restores focus on close. */
export const useFocusTrap = (containerRef, active) => {
  useEffect(() => {
    if (!active) return undefined
    const container = containerRef.current
    if (!container) return undefined

    const previouslyFocused = document.activeElement
    const focusables = () =>
      Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (node) => node.offsetParent !== null || node === document.activeElement
      )

    const initial = focusables()[0] || container
    initial.focus?.({ preventScroll: true })

    const onKeyDown = (event) => {
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    container.addEventListener('keydown', onKeyDown)
    return () => {
      container.removeEventListener('keydown', onKeyDown)
      previouslyFocused?.focus?.({ preventScroll: true })
    }
  }, [active, containerRef])
}

const BREAKPOINTS = { xs: 0, sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1600 }

/**
 * Mirrors antd `Grid.useBreakpoint()`: a map of breakpoint name to whether the
 * viewport is at least that wide. antd's breakpoints, not Tailwind's.
 */
export const useBreakpoint = () => {
  const read = () => {
    const width = typeof window === 'undefined' ? 0 : window.innerWidth
    return Object.fromEntries(
      Object.entries(BREAKPOINTS).map(([name, min]) => [name, width >= min])
    )
  }

  const [screens, setScreens] = useState(read)

  useEffect(() => {
    const onResize = () => setScreens(read())
    window.addEventListener('resize', onResize)
    onResize()
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return screens
}

export { BREAKPOINTS }
